import {
  computed,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Injector,
  signal,
  ViewChild,
} from "@angular/core";
import { DatePipe } from "@angular/common";
import { DateRange } from "@angular/material/datepicker";
import { isEqual } from "date-fns";
import { ViewsService } from "../services/views/views.service";
import { View } from "../models/enum/view.enum";
import { PeriodType } from "../models/types/filter.types";
import { ScenarioStoreService } from "../services/store/scenario/scenario.store.service";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { PeriodSelectionController } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/period.control";
import { focusMovedOutside } from "@bitwarden/angular/utils/focus-moved-outside";

import {
  escapeBelongsToInnerDropdown,
  refocusPickerTrigger,
} from "../gloss/views/views-control/views-datepicker/datepicker-utils";

export const PICKER_TYPE = {
  START: "start",
  END: "end",
  BOTH: "both",
} as const;

@Directive()
export abstract class ViewsDatepickerDirective {
  showStartPicker = false;
  showEndPicker = false;
  protected calculationTodayStoreService = inject(CalculationTodayStoreService);
  protected calculationPastStoreService = inject(CalculationPastStoreService);
  protected scenarioStore = inject(ScenarioStoreService);
  protected viewsService: ViewsService = inject(ViewsService);
  protected readonly PICKER_TYPE = PICKER_TYPE;
  private injector = inject(Injector);

  get isPickerOpen(): boolean {
    return this.showStartPicker || this.showEndPicker;
  }

  protected constructor(
    protected datePipe: DatePipe,
    protected elementRef: ElementRef,
  ) {}

  protected abstract updateDate(dateRange: PeriodType): void;
  @ViewChild("startPickerTrigger", { read: ElementRef }) startPickerTrigger?: ElementRef;
  @ViewChild("endPickerTrigger", { read: ElementRef }) endPickerTrigger?: ElementRef;

  /** The trigger that opens `picker`, to hand focus back to when it closes (#6245). */
  protected pickerTrigger(picker: "start" | "end"): HTMLElement | undefined {
    return (picker === "start" ? this.startPickerTrigger : this.endPickerTrigger)?.nativeElement;
  }

  // period type from store
  currentDateRange = this.getCurrentDateRange();

  // date range type for mat-calendar
  initialDateRange = computed(() => {
    return new DateRange(this.currentDateRange().startDate, this.currentDateRange().endDate);
  });

  // Staged pick shown in the trigger while a picker is open. Null = show the applied
  // (store) range. Set on every in-calendar change, cleared on confirm/cancel/close.
  pendingRange = signal<{ startDate: Date | null; endDate: Date | null } | null>(null);

  // display date text todo: format use preference
  displayDateText = computed(() => {
    const range = this.pendingRange() ?? this.currentDateRange();
    return {
      startDate: this.datePipe.transform(range.startDate, "yyyy-MM-dd"),
      endDate: this.datePipe.transform(range.endDate, "yyyy-MM-dd"),
    };
  });

  /**
   * Not a computed(): the PAST branch's default selection is capped relative to the current
   * wall-clock time (PastPeriodController.getDefaultSelection()), which isn't an Angular signal.
   * A computed() would memoize that cap and go stale if left open across the cap boundary with
   * no other tracked signal changing, so the underlying value is recomputed on every call.
   *
   * The underlying `getDefaultSelection()` call still runs on every invocation — this cache
   * does not skip that computation, only the allocation of a new `DateRange` wrapper. It exists
   * because this method is read directly from template bindings, and some subclasses call
   * ChangeDetectorRef.detectChanges() from ngAfterViewChecked(), which re-evaluates this binding
   * a second time within the same check cycle. Returning a new object/Date instance on every
   * call — even when the logical value hasn't changed — makes Angular's dev-mode change
   * verification see a "different" value between those two reads and throw
   * ExpressionChangedAfterItHasBeenCheckedError. Reusing the previous DateRange when the value is
   * unchanged avoids that while still tracking wall-clock changes across calls.
   */
  private static readonly EMPTY_DATE_RANGE = new DateRange<Date>(null, null);
  private originalDateRangeCache?: DateRange<Date>;

  onDatePreview(event: DateRange<Date>): void {
    this.pendingRange.set(event ? { startDate: event.start, endDate: event.end } : null);
  }

  /** Single source of truth for the view-to-period-controller mapping, shared by
   * resetOriginalDateRange() and getCurrentDateRange() so they can't drift out of sync. */
  private getPeriodControllerForView(): PeriodSelectionController {
    switch (this.viewsService.view) {
      case View.TODAY:
        return this.calculationTodayStoreService.period;
      case View.PAST:
        return this.calculationPastStoreService.period;
      case View.FUTURE:
        return this.scenarioStore.period;
      default:
        throw new Error(`Unsupported view: ${this.viewsService.view}`);
    }
  }

  resetOriginalDateRange(): DateRange<Date> {
    if (this.viewsService.view === View.TODAY) {
      // no picker in todays view yet
      return ViewsDatepickerDirective.EMPTY_DATE_RANGE;
    }

    const originalDateRange = this.getPeriodControllerForView().getDefaultSelection();

    const cache = this.originalDateRangeCache;
    if (
      cache &&
      isEqual(cache.start, originalDateRange.startDate) &&
      isEqual(cache.end, originalDateRange.endDate)
    ) {
      return cache;
    }

    const range = new DateRange(originalDateRange.startDate, originalDateRange.endDate);
    this.originalDateRangeCache = range;
    return range;
  }

  getCurrentDateRange() {
    return computed(() => this.getPeriodControllerForView().selected());
  }

  onDateChange(event: DateRange<Date>, dateType: string): void {
    // It closes the picker, so it hands focus back - not left to the confirm handlers, which run
    // after it only because app-calendar emits confirmEvent next.
    this.refocusTrigger(dateType === "startDate" ? "start" : "end");
    const selectedPeriod: PeriodType = {
      startDate: event.start,
      endDate: event.end,
      _isDefault: false,
    };

    if (dateType === "startDate") {
      this.showStartPicker = false;
    }
    if (dateType === "endDate") {
      this.showEndPicker = false;
    }

    this.updateDate(selectedPeriod);
    this.pendingRange.set(null);
  }

  // Confirm emits dateChangeEvent (onDateChange, which already closed the picker and handed focus
  // back) and then confirmEvent: only refocus here if the picker is still open.
  onEndPickerConfirm() {
    if (this.showEndPicker) {
      this.refocusTrigger("end");
    }
    this.showEndPicker = false;
  }

  onPickerCancel() {
    this.refocusTrigger();
    this.pendingRange.set(null);
    this.showStartPicker = false;
    this.showEndPicker = false;
  }

  onStartPickerConfirm() {
    if (this.showStartPicker) {
      this.refocusTrigger("start");
    }
    this.showStartPicker = false;
  }

  togglePickersVisibility(pickerType: (typeof PICKER_TYPE)[keyof typeof PICKER_TYPE]) {
    // Clear staged pick on every toggle that opens or closes a picker — START/END also close one
    // (re-click its own trigger), and leaving pendingRange set would decouple the trigger from the
    // store. Not on Future's fixed START, which toggles nothing: it wiped an unconfirmed To pick
    // while the open calendar still showed it (#6245).
    if (pickerType === PICKER_TYPE.START && this.viewsService.view === View.FUTURE) {
      return;
    }
    this.pendingRange.set(null);
    switch (pickerType) {
      case PICKER_TYPE.START:
        this.showEndPicker = false;
        this.showStartPicker = !this.showStartPicker;
        break;
      case PICKER_TYPE.END:
        this.showStartPicker = false;
        this.showEndPicker = !this.showEndPicker;
        break;
      case PICKER_TYPE.BOTH:
        this.showStartPicker = false;
        this.showEndPicker = false;
        break;
    }
  }

  closeCalendar() {
    this.pendingRange.set(null);
    this.showStartPicker = false;
    this.showEndPicker = false;
  }

  // On the document, like the other popups' appClickOutside: focus may not be in the picker (a
  // mouse click on the calendar's text, or Safari not focusing clicked buttons). Unconfirmed
  // picks are dropped, as on Cancel.
  @HostListener("document:keydown.escape", ["$event"])
  onEscape(event?: KeyboardEvent) {
    const host: HTMLElement = this.elementRef.nativeElement;
    // Already handled by whatever it was meant for.
    if (event?.defaultPrevented) {
      return;
    }
    if (this.isPickerOpen && !escapeBelongsToInnerDropdown(host, event)) {
      this.refocusTrigger();
      this.closeCalendar();
    }
  }

  /** Which picker is open, if any. */
  private openPicker(): "start" | "end" | undefined {
    return this.showStartPicker ? "start" : this.showEndPicker ? "end" : undefined;
  }

  /** Call before closing `picker` (default: the open one); see refocusPickerTrigger. */
  private refocusTrigger(picker = this.openPicker()): void {
    if (picker) {
      refocusPickerTrigger(this.pickerTrigger(picker), this.injector);
    }
  }

  @HostListener("document:mouseup", ["$event"])
  onClick(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeCalendar();
    }
  }

  // Keyboard counterpart of the outside click above: Tab past the open picker's last control (or
  // Shift+Tab before its trigger) closes it the same way, dropping the unconfirmed pick.
  @HostListener("focusout", ["$event"])
  onFocusOut(event: FocusEvent) {
    if (this.isPickerOpen && focusMovedOutside(event, this.elementRef.nativeElement)) {
      this.closeCalendar();
    }
  }
}
